import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, functions } from '../firebase/firebaseConfig';
import { DayliteContactSimple } from 'shared/types/dayliteModels';
import { getAccountTypeFromCategory } from '../firebase/auth';
import { User, updateProfile } from 'firebase/auth';
import { extractIdFromSelf } from 'src/hooks/useNavToEvent';

/**
 * Checks if a client has access to any events via Cloud Function
 * Uses admin privileges to bypass Firestore security rules during sign-up
 * @param dayliteId The client's Daylite ID
 * @returns true if the client has access to at least one event
 */
const checkClientEventAccess = async (dayliteId: string): Promise<boolean> => {
  try {
    const checkClientEventAccessFn = httpsCallable(functions, 'checkClientEventAccess');
    const response = await checkClientEventAccessFn({ dayliteId });
    const result = response.data as { success: boolean; hasAccess: boolean; error?: string };
    
    if (!result.success) {
      console.error('Error from checkClientEventAccess:', result.error);
      return false;
    }
    
    return result.hasAccess;
  } catch (error) {
    console.error('Error checking client event access:', error);
    return false;
  }
};

interface CheckDayliteAccessResult {
  success: boolean;
  error: string | null;
  accountType?: string;
  dayliteAuthorized?: boolean;
  dayliteId?: string;
  firstName?: string; // Keep for potential direct use if needed elsewhere
  lastName?: string;  // Keep for potential direct use if needed elsewhere
  dayliteContact?: DayliteContactSimple; // Add the contact object itself
}

export const useCheckDayliteAccess = () => {
  /**
   * Checks if a user has access based on their Daylite contact information
   * @param user Firebase User object or object with email
   * @returns Object with success status, error message if any, and authorization status
   */
  const checkDayliteAccess = async (user: User | { email: string }): Promise<CheckDayliteAccessResult> => {
    const fetchContactByEmailFn = httpsCallable(functions, 'fetchContactByEmail');
    const email = user.email;

    // If we have a Firebase User object with UID, check if they already have access
    if ('uid' in user) {
      const accessDocRef = doc(db, 'access', user.uid);
      const accessDocSnap = await getDoc(accessDocRef);

      // User already has access
      if (accessDocSnap.exists()) {
        const accessData = accessDocSnap.data() as DayliteContactSimple & { accountType: string }; // Cast to include accountType
        const dayliteId = extractIdFromSelf(accessData.self);
        return {
          success: true,
          error: null,
          firstName: accessData.first_name,
          lastName: accessData.last_name,
          accountType: accessData.accountType,
          dayliteId,
          dayliteContact: accessData // Return existing contact data
        };
      }
    }

    // User needs to be checked against Daylite
    try {
      // Get contact from Daylite
      const response = await fetchContactByEmailFn({ email });
      const dayliteData = response.data as { success: boolean; data?: DayliteContactSimple; error?: string };

      // Check if there was an error from the Cloud Function
      if (dayliteData && dayliteData.success === false && dayliteData.error) {
        console.error('Daylite access error:', dayliteData.error);
        return {
          success: false,
          error: `Error checking access: ${dayliteData.error}`,
          accountType: '',
          dayliteId: '',
          firstName: '',
          lastName: ''
        };
      }

      // Check if we have a valid contact with the required info
      if (dayliteData?.success && dayliteData?.data?.self) {
        const contact = dayliteData.data;
        const accountType = getAccountTypeFromCategory(contact.category || '');
        const { first_name, last_name } = contact;
        const dayliteId = extractIdFromSelf(contact.self);

        console.log('Daylite access check:', { category: contact.category, accountType, dayliteId });

        // Valid account type found - this user is authorized
        if (accountType) {
          // For Client accounts, also verify they have access to at least one event
          if (accountType === 'Client') {
            console.log('Checking client event access for dayliteId:', dayliteId);
            const hasEventAccess = await checkClientEventAccess(dayliteId);
            console.log('Client event access result:', hasEventAccess);
            if (!hasEventAccess) {
              return {
                success: false,
                error: 'No event access found. Please contact your event coordinator.',
                dayliteAuthorized: false
              };
            }
          }

          // If this is a full Firebase user with UID, create the access record immediately
          // This simplifies the signup flow as the access doc is ready when needed
          if ('uid' in user) {
            console.log('Creating access document during check for user:', user.uid);
            await createAccessDocument(user, contact, accountType); // Call createAccessDocument here
          }

          return {
            success: true,
            error: null,
            accountType,
            dayliteId,
            firstName: first_name,
            lastName: last_name,
            dayliteAuthorized: true,
            dayliteContact: contact // Return the contact data
          };
        }
      }

      // No valid account type or contact found
      return {
        success: false,
        error: 'Access denied. Please contact support.',
        dayliteAuthorized: false
      };
    } catch (error) {
      console.error('Error checking Daylite:', error);
      return {
        success: false,
        error: `Error checking access: ${error}`,
        dayliteAuthorized: false
      };
    }
  };

  /**
   * Creates an access document for a user that was previously verified with Daylite
   * @param user Firebase User with UID
   * @param contact Daylite contact information
   * @param accountType The determined account type
   */
  const createAccessDocument = async (user: User, contact: DayliteContactSimple, accountType: string) => {
    if (!user.uid) {
      return { success: false, error: 'User must have a UID to create access document' };
    }

    try {
      const accessDocRef = doc(db, 'access', user.uid);
      await setDoc(accessDocRef, {
        ...contact,
        email: user.email,
        accountType: accountType,
        createdAt: Timestamp.now()
      });
      return { success: true };
    } catch (error) {
      console.error('Error creating access document:', error);
      return { success: false, error };
    }
  };

  return { checkDayliteAccess, createAccessDocument };
};
